import { message } from '@maxtropy/components';
import {
  apiV2EnergyOverviewConfigSavePost,
  V2EnergyOverviewConfigDetailPostResponse,
} from '@maxtropy/device-customer-apis-v2';

export function initFormValues(res: V2EnergyOverviewConfigDetailPostResponse) {
  // 处理用能统计单元数据
  const getStatisticUnit = (statistic?: any) =>
    statistic?.energyUnitGroup
      ? {
          energyUnitGroupId: statistic.energyUnitGroup.id,
          energyUnitGroupName: statistic.energyUnitGroup.name,
          energyUnitList:
            statistic.energyUnit?.map((unit: any) => ({
              energyUnitIds: unit.id,
              energyUnitName: unit.name,
            })) || [],
        }
      : undefined;

  const energyStatisticsUnits = [
    {
      unit: getStatisticUnit(res.electricityStatistic),
      medium: res.electricityStatistic?.mediums?.map((m: any) => m.id) || [],
    },
    {
      unit: getStatisticUnit(res.waterStatistic),
      medium: res.waterStatistic?.mediums?.map((m: any) => m.id) || [],
    },
    {
      unit: getStatisticUnit(res.gasStatistic),
      medium: res.gasStatistic?.mediums?.map((m: any) => m.id) || [],
    },
  ];

  // 处理能流配置数据
  const energyFlowData: any = {};
  res.energyFlowConfigs?.forEach((config, index) => {
    const idx = index + 1;
    energyFlowData[`energyGroupId${idx}`] = config.energyGroup?.id;
    energyFlowData[`levelRange${idx}`] = {
      fromLevel: config.fromLevel,
      toLevel: config.toLevel,
    };
    energyFlowData[`mediumId${idx}`] = config.medium?.id;
    energyFlowData[`showEnergyGroupName${idx}`] = config.showEnergyGroupName;
  });

  // 处理区域列表配置数据
  const areaList =
    res.areaEnergyUnitMediumConfigList?.map((item, index) => ({
      id: `area-${index}-${item.energyUnitId}`,
      areaName: item.areaName || item.energyUnitName || '',
      energyUnitId: item.energyUnitId,
      energyUnitName: item.energyUnitName || '',
      energyUnitGroupId: item.energyGroupId,
      energyUnitGroupName: item.energyGroupName || '',
      electricMeter: item.electricMediumId,
      waterMeter: item.waterMediumId,
      gasMeters: item.gasMediumId || [],
    })) || [];

  // 处理部门列表配置数据
  const deptList =
    res.deptEnergyUnitMediumConfigList?.map((item, index) => ({
      id: `dept-${index}-${item.energyUnitId}`,
      deptName: item.deptName || item.energyUnitName || '',
      energyUnitId: item.energyUnitId,
      energyUnitName: item.energyUnitName || '',
      energyUnitGroupId: item.energyGroupId,
      energyUnitGroupName: item.energyGroupName || '',
      electricMeter: item.electricMediumId,
      waterMeter: item.waterMediumId,
      gasMeters: item.gasMediumId || [],
    })) || [];

  // 处理环保配置数据 - 按设备分组
  const envDeviceMap = new Map<number, any[]>();
  res.deviceDataPropertyConfigList?.forEach(item => {
    if (item.deviceId) {
      if (!envDeviceMap.has(item.deviceId)) {
        envDeviceMap.set(item.deviceId, []);
      }
      envDeviceMap.get(item.deviceId)?.push({
        id: `dp-${item.deviceId}-${item.dataPropertyId}`,
        dataPropertyId: item.dataPropertyId,
        dataPropertyName: '', // 详情接口不返回名称
        measurementType: item.dataPropertyType === 1 ? 1 : 0, // 1累积量，2瞬时量
        dimension: item.timeResolution, // 直接使用后端返回的字符串值
        indicatorName: item.indicatorName,
      });
    }
  });

  const envDeviceList = Array.from(envDeviceMap.entries()).map(([deviceId, dataPoints], index) => ({
    id: `env-${index}-${deviceId}`,
    deviceId: deviceId,
    deviceName: res.deviceDataPropertyConfigList?.find(d => d.deviceId === deviceId)?.deviceName || '',
    dataPoints: dataPoints,
  }));

  // 处理节能降碳数据
  const energySavingContent = res.energySaving || '';

  const getInspectionConfigs = (
    configs?: {
      id?: number;
      name?: string;
      visibleLightRecorderId?: number;
      infraredRecorderId?: number;
    }[]
  ) =>
    configs?.map(item => ({
      id: item.id,
      name: item.name,
      visibleLightRecorderId: item.visibleLightRecorderId,
      infraredRecorderId: item.infraredRecorderId,
    })) || [];

  return {
    energyStatisticsUnits,
    periodId: res.period?.id,
    nonPeriodId: res.nonPeriod?.id,
    pvCircuitId: res.pvCircuit?.map(c => c.id) || [],
    robotConfigs: getInspectionConfigs(res.robotConfigs),
    dogConfigs: getInspectionConfigs(res.dogConfigs),
    inspectionRecord: res.inspectionRecord?.map(r => r.id) || [],
    ...energyFlowData,
    areaList,
    deptList,
    envDeviceList,
    energySavingContent,
  };
}

export function handleOnFinish(values: any, { setIsEdit, setRefresh, form }: any) {
  // 处理用电/水/气统计
  const getStatistic = (idx: number) => {
    const unitData = values.energyStatisticsUnits?.[idx];
    if (!unitData?.unit && !unitData?.medium) return undefined;
    const mediumValue = unitData?.medium;
    return {
      energyUnitGroupId: unitData?.unit?.energyUnitGroupId || null,
      energyUnitIds: unitData?.unit?.energyUnitList?.map((i: any) => i.energyUnitIds) || [],
      mediumIds: Array.isArray(mediumValue) ? mediumValue : mediumValue ? [mediumValue] : [],
    };
  };

  // 能流配置
  const energyFlowConfigs = Array.from({ length: 5 }, (_, i) => {
    const idx = i + 1;
    const groupId = values[`energyGroupId${idx}`];
    const levelRange = values[`levelRange${idx}`];
    const mediumId = values[`mediumId${idx}`];
    const showName = values[`showEnergyGroupName${idx}`];

    if (!groupId) return null;

    return {
      energyGroupId: groupId,
      fromLevel: levelRange?.fromLevel,
      toLevel: levelRange?.toLevel,
      mediumId: mediumId,
      showEnergyGroupName: showName,
    };
  }).filter(Boolean) as {
    energyGroupId?: number;
    fromLevel?: number;
    toLevel?: number;
    mediumId?: number;
    showEnergyGroupName?: string;
  }[];

  // 区域列表配置
  const areaEnergyUnitMediumConfigList =
    values.areaList?.map((item: any) => ({
      energyGroupId: item.energyUnitGroupId,
      energyUnitId: item.energyUnitId,
      energyUnitName: item.energyUnitName,
      areaName: item.areaName || item.energyUnitName,
      electricMediumId: item.electricMeter,
      waterMediumId: item.waterMeter,
      gasMediumId: item.gasMeters,
    })) || [];

  // 部门列表配置
  const deptEnergyUnitMediumConfigList =
    values.deptList?.map((item: any) => ({
      energyGroupId: item.energyUnitGroupId,
      energyUnitId: item.energyUnitId,
      energyUnitName: item.energyUnitName,
      deptName: item.deptName || item.energyUnitName,
      electricMediumId: item.electricMeter,
      waterMediumId: item.waterMeter,
      gasMediumId: item.gasMeters,
    })) || [];

  // 环保配置 - 将设备+数据点平铺为列表
  const deviceDataPropertyConfigList: any[] = [];
  values.envDeviceList?.forEach((device: any) => {
    device.dataPoints?.forEach((dp: any) => {
      deviceDataPropertyConfigList.push({
        deviceId: device.deviceId,
        dataPropertyId: dp.dataPropertyId,
        dataPropertyType: dp.measurementType === 1 ? 1 : 2, // 1累积量，2瞬时量
        timeResolution: dp.dimension,
        indicatorName: dp.indicatorName,
      });
    });
  });

  // 节能降碳
  const energySaving = values.energySavingContent || '';

  const getInspectionConfigs = (configs?: any[]) =>
    configs
      ?.filter(item => item?.name || item?.visibleLightRecorderId || item?.infraredRecorderId)
      .map(item => ({
        id: item.id,
        name: item.name,
        visibleLightRecorderId: item.visibleLightRecorderId,
        infraredRecorderId: item.infraredRecorderId,
      })) || [];

  return apiV2EnergyOverviewConfigSavePost({
    electricityStatistic: getStatistic(0),
    waterStatistic: getStatistic(1),
    gasStatistic: getStatistic(2),
    periodId: values.periodId,
    nonPeriodId: values.nonPeriodId,
    pvCircuitId: values.pvCircuitId,
    robotConfigs: getInspectionConfigs(values.robotConfigs),
    dogConfigs: getInspectionConfigs(values.dogConfigs),
    inspectionRecordIds: values.inspectionRecord,
    energyFlowConfigs,
    areaEnergyUnitMediumConfigList,
    deptEnergyUnitMediumConfigList,
    deviceDataPropertyConfigList,
    energySaving,
  }).then(() => {
    message.success('保存成功');
    setIsEdit(false);
    setRefresh((prev: boolean) => !prev);
  });
}
